import { CheckCircle, Target, Award, Users, Car, Plane, Layers } from 'lucide-react';
import Footer from '../components/Footer';

const About = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        data-testid="about-hero-section"
        className="relative py-32 lg:py-40 overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(to bottom, rgba(26,29,36,0.95), rgba(26,29,36,1)), url('https://static.prod-images.emergentagent.com/jobs/9e999b6a-85cd-4b74-86b8-71790862301d/images/5f84abd9884d9887366963d63ccc6f610aa16e018842da90f8e94a97dc198693.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-omni-accent mb-6">
            About Omnimet
          </div>
          <h1
            data-testid="about-headline"
            className="text-5xl lg:text-7xl font-heading font-light tracking-tighter text-white mb-6 leading-[1.1] max-w-4xl"
          >
            Engineering Excellence Through Precision and Quality
          </h1>
          <p className="text-base lg:text-lg text-omni-text-secondary leading-relaxed max-w-3xl">
            Omnimet Private Limited is a premier global supplier of high-grade metallic materials
            and precision-engineered components, serving the world's most demanding industries.
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section data-testid="story-section" className="py-24 lg:py-32 bg-omni-background">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left: Text */}
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-omni-accent mb-4">
                Our Commitment
              </div>
              <h2
                data-testid="story-headline"
                className="text-4xl lg:text-5xl font-heading font-normal tracking-tight text-white mb-6"
              >
                Global Quality Compliance
              </h2>
              <p className="text-base text-omni-text-secondary leading-relaxed mb-6">
                For over a decade, Omnimet has established itself as a trusted partner for
                aerospace and automotive manufacturers worldwide. Our unwavering commitment to
                quality, precision, and reliability has made us the preferred choice for companies
                requiring materials that meet the most stringent international standards.
              </p>
              <p className="text-base text-omni-text-secondary leading-relaxed mb-6">
                We specialize in the supply and contract manufacturing of premium Aluminium and
                Stainless Steel Round Bars and Pipes, engineered to handle critical tolerances and
                extreme operational environments.
              </p>
              <p className="text-base text-omni-text-secondary leading-relaxed">
                Every material we supply comes with full traceability documentation, certified mill
                test reports, and compliance certificates ensuring adherence to ASTM, EN, DIN, and
                other international metallurgical standards.
              </p>
            </div>

            {/* Right: Image */}
            <div className="relative">
              <div className="aspect-[4/3] overflow-hidden border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.4)]">
                <img
                  src="https://images.unsplash.com/photo-1705614681501-a7f60c15bc97?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxhZXJvc3BhY2UlMjBlbmdpbmVlcmluZyUyMG1hbnVmYWN0dXJpbmd8ZW58MHx8fHwxNzc5MzAwNTM0fDA&ixlib=rb-4.1.0&q=85"
                  alt="Aerospace Engineering"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-omni-accent/10 border border-omni-accent/30 backdrop-blur-sm" />
            </div>
          </div>
        </div>
      </section>

      {/* Industries Served Section - Aerospace */}
      <section
        data-testid="industry-aerospace-section"
        className="py-24 lg:py-32 bg-omni-background-secondary"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left: Image */}
            <div className="relative order-2 lg:order-1">
              <div className="aspect-[4/3] overflow-hidden border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.4)]">
                <img
                  src="https://images.unsplash.com/photo-1556388158-158ea5ccacbd?crop=entropy&cs=srgb&fm=jpg&q=85&w=940"
                  alt="Aerospace Industry"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -top-6 -left-6 w-48 h-48 bg-omni-accent/10 border border-omni-accent/30 backdrop-blur-sm -z-10" />
            </div>

            {/* Right: Text */}
            <div className="order-1 lg:order-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-omni-accent/10 flex items-center justify-center">
                  <Plane className="w-5 h-5 text-omni-accent" />
                </div>
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-omni-accent">
                  Aerospace Industry
                </div>
              </div>
              <h2 className="text-4xl lg:text-5xl font-heading font-normal tracking-tight text-white mb-6">
                Built for the Skies
              </h2>
              <p className="text-base text-omni-text-secondary leading-relaxed mb-6">
                Our high-strength aluminum alloys and stainless steel components meet the
                stringent specifications demanded by aerospace defense contractors and commercial
                aviation manufacturers. From critical structural components to engine parts, we
                deliver materials engineered for the harshest operational environments.
              </p>
              <ul className="space-y-3 text-sm text-omni-text-secondary">
                <li className="flex items-center gap-3">
                  <div className="w-1.5 h-1.5 bg-omni-accent" />
                  AS9100 compliant manufacturing standards
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-1.5 h-1.5 bg-omni-accent" />
                  High-strength aerospace-grade aluminum (7075, 2024)
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-1.5 h-1.5 bg-omni-accent" />
                  Full traceability and mill test certifications
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-1.5 h-1.5 bg-omni-accent" />
                  Tight tolerances for safety-critical applications
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Industries Served Section - Automotive */}
      <section
        data-testid="industry-automotive-section"
        className="py-24 lg:py-32 bg-omni-background"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left: Text */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-omni-accent/10 flex items-center justify-center">
                  <Car className="w-5 h-5 text-omni-accent" />
                </div>
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-omni-accent">
                  Automotive Industry
                </div>
              </div>
              <h2 className="text-4xl lg:text-5xl font-heading font-normal tracking-tight text-white mb-6">
                Engineered for the Road
              </h2>
              <p className="text-base text-omni-text-secondary leading-relaxed mb-6">
                Precision-engineered round bars and custom components for Tier-1 automotive
                suppliers and OEMs. Our materials power critical engine, transmission, and chassis
                components in everything from passenger vehicles to high-performance racing
                applications and commercial trucks.
              </p>
              <ul className="space-y-3 text-sm text-omni-text-secondary">
                <li className="flex items-center gap-3">
                  <div className="w-1.5 h-1.5 bg-omni-accent" />
                  IATF 16949 compliant production processes
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-1.5 h-1.5 bg-omni-accent" />
                  Custom valve components and fittings
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-1.5 h-1.5 bg-omni-accent" />
                  High-volume precision CNC turning capabilities
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-1.5 h-1.5 bg-omni-accent" />
                  Stress-tested materials for performance applications
                </li>
              </ul>
            </div>

            {/* Right: Image */}
            <div className="relative">
              <div className="aspect-[4/3] overflow-hidden border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.4)]">
                <img
                  src="https://images.pexels.com/photos/36598766/pexels-photo-36598766.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Automotive Industry"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-omni-accent/10 border border-omni-accent/30 backdrop-blur-sm -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* Industries Served Section - Die & Mould */}
      <section
        data-testid="industry-die-mould-section"
        className="py-24 lg:py-32 bg-omni-background-secondary"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left: Image */}
            <div className="relative order-2 lg:order-1">
              <div className="aspect-[4/3] overflow-hidden border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.4)]">
                <img
                  src="https://static.prod-images.emergentagent.com/jobs/9e999b6a-85cd-4b74-86b8-71790862301d/images/d9f891249d0e86b9d2d4eb6a2c9f94ea4d7762dcc0d46494199ec2369f2bf13f.png"
                  alt="Die and Mould Industry"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -top-6 -left-6 w-48 h-48 bg-omni-accent/10 border border-omni-accent/30 backdrop-blur-sm -z-10" />
            </div>

            {/* Right: Text */}
            <div className="order-1 lg:order-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-omni-accent/10 flex items-center justify-center">
                  <Layers className="w-5 h-5 text-omni-accent" />
                </div>
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-omni-accent">
                  Die & Mould Industry
                </div>
              </div>
              <h2 className="text-4xl lg:text-5xl font-heading font-normal tracking-tight text-white mb-6">
                Precision Aluminium Plates
              </h2>
              <p className="text-base text-omni-text-secondary leading-relaxed mb-6">
                Premium-grade aluminium plates engineered specifically for die and mould
                manufacturing applications. Our high-stability cast aluminium plates deliver
                exceptional dimensional accuracy, superior machinability, and consistent
                performance for tooling, injection moulds, and precision dies.
              </p>
              <ul className="space-y-3 text-sm text-omni-text-secondary">
                <li className="flex items-center gap-3">
                  <div className="w-1.5 h-1.5 bg-omni-accent" />
                  High-stability cast aluminium plates (5083, 6082, 7075)
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-1.5 h-1.5 bg-omni-accent" />
                  Stress-relieved for distortion-free machining
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-1.5 h-1.5 bg-omni-accent" />
                  Excellent thermal conductivity for mould applications
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-1.5 h-1.5 bg-omni-accent" />
                  Available in thickness from 6mm to 200mm
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Core Capabilities */}
      <section
        data-testid="capabilities-section"
        className="py-24 lg:py-32 bg-omni-background"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-omni-accent mb-4">
              What Sets Us Apart
            </div>
            <h2 className="text-4xl lg:text-5xl font-heading font-normal tracking-tight text-white">
              Core Capabilities
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Capability 1 */}
            <div
              data-testid="capability-card-testing"
              className="bg-omni-background-secondary border border-white/10 p-8 hover:border-omni-accent/50 transition-colors"
            >
              <div className="w-12 h-12 bg-omni-accent/10 flex items-center justify-center mb-6">
                <CheckCircle className="w-6 h-6 text-omni-accent" />
              </div>
              <h3 className="text-xl font-heading font-medium text-white mb-3">
                Advanced Material Testing
              </h3>
              <p className="text-sm text-omni-text-secondary leading-relaxed">
                In-house and third-party testing facilities ensuring chemical composition,
                mechanical properties, and structural integrity compliance.
              </p>
            </div>

            {/* Capability 2 */}
            <div
              data-testid="capability-card-precision"
              className="bg-omni-background-secondary border border-white/10 p-8 hover:border-omni-accent/50 transition-colors"
            >
              <div className="w-12 h-12 bg-omni-accent/10 flex items-center justify-center mb-6">
                <Target className="w-6 h-6 text-omni-accent" />
              </div>
              <h3 className="text-xl font-heading font-medium text-white mb-3">
                Cutting-to-Length Accuracy
              </h3>
              <p className="text-sm text-omni-text-secondary leading-relaxed">
                Precision cutting services with dimensional tolerances maintained to ±0.5mm,
                reducing material waste and machining time.
              </p>
            </div>

            {/* Capability 3 */}
            <div
              data-testid="capability-card-cnc"
              className="bg-omni-background-secondary border border-white/10 p-8 hover:border-omni-accent/50 transition-colors"
            >
              <div className="w-12 h-12 bg-omni-accent/10 flex items-center justify-center mb-6">
                <Award className="w-6 h-6 text-omni-accent" />
              </div>
              <h3 className="text-xl font-heading font-medium text-white mb-3">
                Ultra-Tight CNC Machining
              </h3>
              <p className="text-sm text-omni-text-secondary leading-relaxed">
                Multi-axis CNC turning and milling centers capable of achieving tolerances down to
                ±0.05mm for critical aerospace components.
              </p>
            </div>

            {/* Capability 4 */}
            <div
              data-testid="capability-card-traceability"
              className="bg-omni-background-secondary border border-white/10 p-8 hover:border-omni-accent/50 transition-colors"
            >
              <div className="w-12 h-12 bg-omni-accent/10 flex items-center justify-center mb-6">
                <Users className="w-6 h-6 text-omni-accent" />
              </div>
              <h3 className="text-xl font-heading font-medium text-white mb-3">
                Full Traceability
              </h3>
              <p className="text-sm text-omni-text-secondary leading-relaxed">
                Complete material traceability from mill source to final component, with detailed
                documentation supporting AS9100 and IATF 16949 compliance.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Clients Section */}
      <section data-testid="clients-section" className="py-24 lg:py-32 bg-omni-background overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 mb-16">
          <div className="text-center">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-omni-accent mb-4">
              Global Partnerships
            </div>
            <h2
              data-testid="clients-headline"
              className="text-4xl lg:text-5xl font-heading font-normal tracking-tight text-white mb-6"
            >
              Our Clients
            </h2>
            <p className="text-base lg:text-lg text-omni-text-secondary leading-relaxed max-w-3xl mx-auto">
              Trusted by industry leaders across automotive, aerospace, and precision manufacturing
              sectors worldwide.
            </p>
          </div>
        </div>

        {/* Marquee Container */}
        <div
          data-testid="clients-marquee"
          className="relative w-full overflow-hidden"
          style={{
            maskImage:
              'linear-gradient(to right, transparent, black 10%, black 90%, transparent)',
            WebkitMaskImage:
              'linear-gradient(to right, transparent, black 10%, black 90%, transparent)',
          }}
        >
          <div className="flex animate-marquee whitespace-nowrap" style={{ width: 'fit-content' }}>
            {[1, 2].map((set) =>
              [
                { name: 'JBM Group', logo: null },
                { name: 'Ford', logo: '/clients/ford.svg' },
                { name: 'Mahindra', logo: '/clients/mahindra.svg' },
                { name: 'Linamar', logo: null },
                { name: 'Magna', logo: '/clients/magna.svg' },
                { name: 'Martinrea', logo: null },
                { name: 'International', logo: '/clients/international.svg' },
                { name: 'CATL', logo: '/clients/catl.svg' },
                { name: 'Denso', logo: '/clients/denso.svg' },
              ].map((client, idx) => (
                <div
                  key={`client-${set}-${idx}`}
                  data-testid={set === 1 ? `client-logo-${idx}` : undefined}
                  aria-hidden={set === 2 ? 'true' : undefined}
                  className="flex items-center justify-center px-10 py-8 mx-4 border border-white/10 bg-omni-background-secondary hover:border-omni-accent/50 transition-colors flex-shrink-0 w-[240px] h-32"
                >
                  {client.logo ? (
                    <img
                      src={client.logo}
                      alt={client.name}
                      className="max-h-16 max-w-[170px] object-contain"
                      style={{ filter: 'brightness(0) invert(1)', opacity: 0.85 }}
                    />
                  ) : (
                    <span className="text-2xl font-heading font-semibold text-white tracking-tight whitespace-nowrap">
                      {client.name}
                    </span>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
